import React, { useState } from 'react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { MapPin, Truck, Navigation, Clock, Package, Tangent as GasTank, Sprout, Leaf, List, Check, X, Play, Pause, MessageSquare, Trash2, Camera, CreditCard as Edit } from 'lucide-react';
import { Task, Client, Driver, Dumpster, User } from '../../types';
import { supabase } from '../../lib/supabase';

interface TaskCardProps {
  task: Task;
  client?: Client;
  driver?: Driver;
  drivers: Driver[];
  users: User[];
  dumpster?: Dumpster;
  onOpenProductsModal?: (task: Task) => void;
  onOpenPhotoSignatureModal?: (task: Task) => void;
  onCancelTask?: (task: Task) => void;
  onCompleteTask?: (task: Task) => void;
  onStartTask?: (task: Task) => void;
  onSetTaskToWaiting?: (task: Task) => void;
  onDeleteTask?: (task: Task) => void;
  onEditTask?: (task: Task) => void;
  canCompleteTask?: boolean;
  isAdmin?: boolean;
  isLogistics?: boolean;
}

const TaskCard: React.FC<TaskCardProps> = ({
  task,
  client,
  driver,
  drivers,
  users,
  dumpster,
  onOpenProductsModal,
  onOpenPhotoSignatureModal,
  onCancelTask,
  onCompleteTask,
  onStartTask,
  onSetTaskToWaiting,
  onDeleteTask,
  onEditTask,
  canCompleteTask,
  isAdmin,
  isLogistics
}) => {
  // Trouver l'utilisateur qui a créé la tâche
  const createdByUser = users.find(user => user.id === task.createdBy);
  const isCreatedByDriver = createdByUser?.role === 'driver';
  const [isSendingNotification, setIsSendingNotification] = useState(false);

  // Déterminer la couleur de fond en fonction du statut et du créateur
  let bgColor = '';
  switch (task.status) {
    case 'pending':
      bgColor = isCreatedByDriver ? 'bg-purple-50 border-purple-100' : 'bg-orange-50 border-orange-100';
      break;
    case 'waiting':
      bgColor = isCreatedByDriver ? 'bg-yellow-50 border-yellow-100' : 'bg-yellow-50 border-yellow-100';
      break;
    case 'in-progress':
      bgColor = isCreatedByDriver ? 'bg-blue-50 border-blue-100' : 'bg-sky-50 border-sky-100';
      break;
    case 'completed':
      bgColor = isCreatedByDriver ? 'bg-emerald-50 border-emerald-100' : 'bg-green-50 border-green-100';
      break;
    case 'cancelled':
      bgColor = isCreatedByDriver ? 'bg-pink-50 border-pink-100' : 'bg-red-50 border-red-100';
      break;
    case 'editing':
      bgColor = 'bg-amber-50 border-amber-100';
      break;
  }

  const handleOpenGoogleMaps = async () => {
    try {
      // Vérifier si nous avons un locationId
      if (task.locationId) {
        const { data: locationData, error } = await supabase
          .from('client_locations')
          .select('position_lat, position_lng')
          .eq('id', task.locationId)
          .single();

        if (error) throw error;

        if (locationData) {
          window.open(
            `https://www.google.com/maps/dir/?api=1&destination=${locationData.position_lat},${locationData.position_lng}`,
            '_blank'
          );
          return;
        }
      }

      // Fallback sur les coordonnées du client si pas de locationId
      if (client?.position) {
        window.open(
          `https://www.google.com/maps/dir/?api=1&destination=${client.position.lat},${client.position.lng}`,
          '_blank'
        );
      } else {
        throw new Error('Aucune coordonnée GPS disponible');
      }
    } catch (error) {
      console.error('Error fetching location:', error);
      alert('Erreur lors de la récupération des coordonnées GPS');
    }
  };

  // Format the actual quantity with 3 decimal places if it's a decimal number
  const formatQuantity = (value: number | null | undefined) => {
    if (value === undefined || value === null) return '';
    
    // Check if the value has decimal places
    if (value % 1 === 0) {
      return value.toString(); // Return as integer if no decimal places
    } else {
      return value.toFixed(3); // Format with 3 decimal places
    }
  };

  // Fonction pour envoyer manuellement une notification SMS
  const handleSendNotification = async () => {
    if (task.status !== 'in-progress' && task.status !== 'completed' && task.status !== 'cancelled') {
      alert('La tâche doit être en cours, terminée ou annulée pour envoyer une notification');
      return;
    }

    try {
      setIsSendingNotification(true);
      
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const { data: sessionData } = await supabase.auth.getSession();
      
      if (!sessionData.session) {
        throw new Error('Session non valide');
      }
      
      // Appeler la fonction Edge process-notifications
      const response = await fetch(`${supabaseUrl}/functions/v1/process-notifications`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionData.session.access_token}`
        }
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Erreur lors du traitement des notifications: ${response.status} ${errorText}`);
      }
      
      const result = await response.json();
      
      // Vérifier si notre tâche a été traitée
      const taskProcessed = result.results?.some((r: any) => r.task_id === task.id);
      
      if (taskProcessed) {
        alert('Notification SMS envoyée avec succès');
      } else {
        // Si la tâche n'a pas été traitée, créer une entrée de notification manuellement
        const { data: notificationData, error: notificationError } = await supabase
          .from('notification_logs')
          .insert({
            task_id: task.id,
            message: {
              status: task.status,
              client_phone: client?.phone,
              client_name: client?.companyName,
              driver_name: `${driver?.firstName} ${driver?.lastName}`,
              task_type: task.type,
              timestamp: new Date().toISOString(),
              needs_notification: true,
              task_id: task.id
            },
            processed: false
          })
          .select();
          
        if (notificationError) {
          throw notificationError;
        }
        
        // Appeler à nouveau la fonction pour traiter cette nouvelle notification
        const secondResponse = await fetch(`${supabaseUrl}/functions/v1/process-notifications`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${sessionData.session.access_token}`
          }
        });
        
        if (!secondResponse.ok) {
          throw new Error('Erreur lors du second traitement des notifications');
        }
        
        alert('Notification SMS créée et envoyée avec succès');
      }
    } catch (error) {
      console.error('Error sending notification:', error);
      const errorMessage = error instanceof Error ? error.message : 'Erreur inconnue';
      alert(`Erreur lors de l'envoi de la notification: ${errorMessage}`);
    } finally {
      setIsSendingNotification(false);
    }
  };

  return (
    <div className={`${bgColor} rounded-lg border-2 shadow p-4 relative`}>
      {isCreatedByDriver && (
        <div className="absolute top-0 right-0 bg-purple-600 text-white text-xs px-2 py-1 rounded-bl-lg rounded-tr-lg">
          Créée par chauffeur
        </div>
      )}
      {task.status === 'editing' && (
        <div className="absolute top-0 left-0 bg-amber-600 text-white text-xs px-2 py-1 rounded-br-lg rounded-tl-lg">
          En modification
        </div>
      )}
      
      <div className="flex justify-between items-start mb-3">
        <div>
          <h3 className="font-medium">{client?.companyName}</h3>
          <p className="text-sm text-gray-600">
            {format(new Date(task.date), 'dd/MM/yyyy HH:mm', { locale: fr })}
          </p>
        </div>
        
        <div className="flex items-center space-x-2">
          {task.type === 'fuel' && <GasTank className="h-5 w-5 text-blue-600" />}
          {task.type === 'dumpster' && <Package className="h-5 w-5 text-blue-600" />}
          {task.type === 'phyto' && (
            <>
              <Sprout className="h-5 w-5 text-green-600" />
              {onOpenProductsModal && (
                <button
                  onClick={() => onOpenProductsModal(task)}
                  className="p-1 hover:bg-gray-100 rounded-full"
                  title="Voir les produits"
                >
                  <List className="h-5 w-5 text-gray-600" />
                </button>
              )}
              {(isAdmin || isLogistics) && onOpenPhotoSignatureModal &&
               ((task.photos && task.photos.length > 0) || task.signature) && (
                <button
                  onClick={() => onOpenPhotoSignatureModal(task)}
                  className="p-1 hover:bg-gray-100 rounded-full"
                  title="Voir photos et signature"
                >
                  <Camera className="h-5 w-5 text-purple-600" />
                </button>
              )}
            </>
          )}
          {task.type === 'engrais' && (
            <>
              <Leaf className="h-5 w-5 text-teal-600" />
              {onOpenProductsModal && (
                <button
                  onClick={() => onOpenProductsModal(task)}
                  className="p-1 hover:bg-gray-100 rounded-full"
                  title="Voir les produits"
                >
                  <List className="h-5 w-5 text-gray-600" />
                </button>
              )}
              {(isAdmin || isLogistics) && onOpenPhotoSignatureModal &&
               ((task.photos && task.photos.length > 0) || task.signature) && (
                <button
                  onClick={() => onOpenPhotoSignatureModal(task)}
                  className="p-1 hover:bg-gray-100 rounded-full"
                  title="Voir photos et signature"
                >
                  <Camera className="h-5 w-5 text-purple-600" />
                </button>
              )}
            </>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <p className="flex items-center text-sm text-gray-600">
          <Truck className="h-4 w-4 mr-2" />
          {driver ? `${driver.firstName} ${driver.lastName}` : 'N/A'}
        </p>

        {client && (
          <div className="flex items-center mb-1">
            <p className="text-sm text-gray-600 flex items-center flex-1">
              <MapPin className="h-4 w-4 mr-1" />
              <span className="break-words">{client.streetAddress}, {client.postalCode} {client.city}</span>
            </p>
          </div>
        )}

        {task.startTime && (
          <p className="text-sm text-gray-600">
            <Clock className="h-4 w-4 inline mr-1" />
            Début: {format(new Date(task.startTime), 'HH:mm', { locale: fr })}
          </p>
        )}

        {task.status === 'completed' && task.endTime && (
          <p className="text-sm text-gray-600">
            <Clock className="h-4 w-4 inline mr-1" />
            Fin: {format(new Date(task.endTime), 'HH:mm', { locale: fr })}
          </p>
        )}

        <div className="mt-3 pt-3 border-t border-gray-200">
          {task.type === 'fuel' && (
            <>
              <p className="text-sm">
                Type: {task.fuelType === 'fioul' ? 'Fioul' : task.fuelType === 'gnr' ? 'GNR' : 'Gasoil'}
              </p>
              <div className="text-sm">
                <p>Litrage prévu: {task.plannedLiters} L</p>
                {task.actualLiters && (
                  <p className={`${task.status === 'completed' ? 'text-green-700 font-medium' : ''}`}>
                    Litrage effectif: {task.actualLiters} L
                  </p>
                )}
              </div>
              <p className="text-sm">Prix: {task.price} €/L</p>
            </>
          )}

          {task.type === 'dumpster' && dumpster && (
            <>
              <p className="text-sm">Benne: {dumpster.name} ({dumpster.number})</p>
              {(dumpster.name === 'Benne Inconnue' || dumpster.number === 'UNKNOWN') && (
                <div className="mt-1 px-2 py-1 bg-yellow-100 text-yellow-800 rounded text-xs">
                  ⚠️ Benne à définir au démarrage
                </div>
              )}
              <p className={`text-sm font-medium px-2 py-1 rounded-lg inline-block mt-1 ${
                task.dumpsterTaskType === 'pose' ? 'bg-green-100 text-green-800' :
                task.dumpsterTaskType === 'retrait' ? 'bg-red-100 text-red-800' :
                task.dumpsterTaskType === 'echange' ? 'bg-blue-100 text-blue-800' : 
                'bg-gray-100 text-gray-800'
              }`}>
                Type: {
                  task.dumpsterTaskType === 'pose' ? 'Pose' :
                  task.dumpsterTaskType === 'retrait' ? 'Retrait' :
                  task.dumpsterTaskType === 'echange' ? 'Échange' : 'N/A'
                }
              </p>
              {task.dropLocation && (
                <p className="text-sm mt-1">
                  Déposée: {task.dropLocation.type === 'client' ? client?.companyName : 'Dépôt'}
                </p>
              )}
              {task.status === 'completed' && task.actualQuantity !== undefined && (
                <p className="text-sm mt-1 text-green-700 font-medium">
                  Quantité effective: {formatQuantity(task.actualQuantity)} tonnes
                </p>
              )}
            </>
          )}

          {task.type === 'phyto' && task.products && task.products.length > 0 && (
            <>
              {task.products.map((product, index) => (
                <div key={index} className="text-sm">
                  <p>Produit: {product.name}</p>
                  <p>Quantité prévue: {product.plannedQuantity}</p>
                  {product.actualQuantity && <p>Quantité effective: {product.actualQuantity}</p>}
                </div>
              ))}
            </>
          )}

          {task.type === 'engrais' && task.products && task.products.length > 0 && (
            <>
              {task.products.map((product, index) => (
                <div key={index} className="text-sm">
                  <p>Produit: {product.name}</p>
                  <p>Quantité prévue: {product.plannedQuantityKg} kg</p>
                  {product.actualQuantityKg && <p>Quantité effective: {product.actualQuantityKg} kg</p>}
                </div>
              ))}
            </>
          )}

          {/* Affichage du commentaire s'il existe */}
          {task.comment && (
            <div className="mt-2 p-2 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex items-start">
                <MessageSquare className="h-4 w-4 text-gray-500 mt-0.5 mr-2 flex-shrink-0" />
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{task.comment}</p>
              </div>
            </div>
          )}

          {task.status === 'completed' && task.finishedBy && (
            <div className="mt-2 text-sm text-green-600">
              Terminée par: {
                (() => {
                  const finishedByUser = users.find(u => u.id === task.finishedBy);
                  return finishedByUser ? finishedByUser.firstName : task.finishedBy;
                })()
              }
            </div>
          )}

          {task.status === 'cancelled' && task.cancelledAt && (
            <div className="mt-2 text-sm text-red-600">
              Annulée le {format(new Date(task.cancelledAt), 'dd/MM/yyyy HH:mm', { locale: fr })}
            </div>
          )}
          
          {isCreatedByDriver && (
            <div className="mt-2 text-sm text-purple-600">
              Créée par: {createdByUser?.firstName} {createdByUser?.lastName}
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div className="mt-4 flex flex-wrap justify-end gap-1.5 sm:gap-2">
          {/* Bouton Itinéraire */}
          <button
            onClick={handleOpenGoogleMaps}
            className="px-2 py-1 sm:px-3 text-blue-600 hover:text-blue-800 flex items-center text-sm rounded-lg"
            title="Ouvrir dans Google Maps"
          >
            <Navigation className="h-4 w-4 mr-1" />
            <span className="hidden sm:inline">Itinéraire</span>
            <span className="sm:hidden">Itin.</span>
          </button>

          {task.status === 'pending' && onEditTask && (isLogistics || isAdmin) && task.type === 'fuel' && (
            <button
              onClick={() => onEditTask(task)}
              className="px-2 py-1 sm:px-3 bg-amber-100 text-amber-700 rounded-lg hover:bg-amber-200 flex items-center text-sm"
            >
              <Edit className="h-4 w-4 mr-1" />
              <span className="hidden sm:inline">Modifier</span>
              <span className="sm:hidden">Mod.</span>
            </button>
          )}

          {task.status === 'pending' && onCancelTask && (
            <button
              onClick={() => onCancelTask(task)}
              className="px-2 py-1 sm:px-3 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 flex items-center text-sm"
            >
              <X className="h-4 w-4 mr-1" />
              <span className="hidden sm:inline">Annuler</span>
              <span className="sm:hidden">Ann.</span>
            </button>
          )}

          {task.status === 'pending' && onStartTask && (
            <button
              onClick={() => onStartTask(task)}
              className="px-2 py-1 sm:px-3 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 flex items-center text-sm"
            >
              <Play className="h-4 w-4 mr-1" />
              <span className="hidden sm:inline">Commencer</span>
              <span className="sm:hidden">Déb.</span>
            </button>
          )}

          {task.status === 'editing' && (
            <div className="px-2 py-1 sm:px-3 bg-amber-100 text-amber-700 rounded-lg flex items-center text-sm">
              En cours de modification
            </div>
          )}

          {task.status === 'waiting' && onCompleteTask && (canCompleteTask || isAdmin) && (
            <button
              onClick={() => onCompleteTask(task)}
              className="px-2 py-1 sm:px-3 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 flex items-center text-sm"
            >
              <Check className="h-4 w-4 mr-1" />
              <span className="hidden sm:inline">{isAdmin ? 'Finir' : 'Terminer'}</span>
              <span className="sm:hidden">Fin</span>
            </button>
          )}

          {task.status === 'in-progress' && (
            <>
              {/* Bouton pour envoyer une notification SMS manuellement */}
              {isAdmin && client?.phone && (
                <button
                  onClick={handleSendNotification}
                  disabled={isSendingNotification}
                  className="px-2 py-1 sm:px-3 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 flex items-center text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSendingNotification ? (
                    <span className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-purple-700\" xmlns="http://www.w3.org/2000/svg\" fill="none\" viewBox="0 0 24 24">
                        <circle className="opacity-25\" cx="12\" cy="12\" r="10\" stroke="currentColor\" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Envoi...
                    </span>
                  ) : (
                    <>
                      <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                      </svg>
                      <span className="hidden sm:inline">Notifier</span>
                      <span className="sm:hidden">SMS</span>
                    </>
                  )}
                </button>
              )}
              
              {/* Bouton pour annuler une tâche en cours (pour admin et logistics) */}
              {onCancelTask && (isAdmin || isLogistics) && (
                <button
                  onClick={() => onCancelTask(task)}
                  className="px-2 py-1 sm:px-3 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 flex items-center text-sm"
                >
                  <X className="h-4 w-4 mr-1" />
                  <span className="hidden sm:inline">Annuler</span>
                  <span className="sm:hidden">Ann.</span>
                </button>
              )}
              
              {onSetTaskToWaiting && (
                <button
                  onClick={() => onSetTaskToWaiting(task)}
                  className="px-2 py-1 sm:px-3 bg-yellow-100 text-yellow-800 rounded-lg hover:bg-yellow-200 flex items-center text-sm"
                >
                  <Pause className="h-4 w-4 mr-1" />
                  <span className="hidden sm:inline">Mettre en attente</span>
                  <span className="sm:hidden">Pause</span>
                </button>
              )}

              {onCompleteTask && (canCompleteTask || isAdmin) && (
                <button
                  onClick={() => onCompleteTask(task)}
                  className="px-2 py-1 sm:px-3 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 flex items-center text-sm"
                >
                  <Check className="h-4 w-4 mr-1" />
                  <span className="hidden sm:inline">{isAdmin ? 'Finir' : 'Terminer'}</span>
                  <span className="sm:hidden">Fin</span>
                </button>
              )}
            </>
          )}
          
          {task.status === 'completed' && (
            <>
              {isAdmin && client?.phone && (
                <button
                  onClick={handleSendNotification}
                  disabled={isSendingNotification}
                  className="px-2 py-1 sm:px-3 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 flex items-center text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSendingNotification ? (
                    <span className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-purple-700\" xmlns="http://www.w3.org/2000/svg\" fill="none\" viewBox="0 0 24 24">
                        <circle className="opacity-25\" cx="12\" cy="12\" r="10\" stroke="currentColor\" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Envoi...
                    </span>
                  ) : (
                    <>
                      <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                      </svg>
                      <span className="hidden sm:inline">Envoyer SMS de fin</span>
                      <span className="sm:hidden">SMS</span>
                    </>
                  )}
                </button>
              )}
              
              {onDeleteTask && isAdmin && (
                <button
                  onClick={() => onDeleteTask(task)}
                  className="px-2 py-1 sm:px-3 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 flex items-center text-sm"
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  <span className="hidden sm:inline">Supprimer</span>
                  <span className="sm:hidden">Supp.</span>
                </button>
              )}
            </>
          )}
          
          {task.status === 'cancelled' && (
            <>
              {onDeleteTask && isAdmin && (
                <button
                  onClick={() => onDeleteTask(task)}
                  className="px-2 py-1 sm:px-3 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 flex items-center text-sm"
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  <span className="hidden sm:inline">Supprimer</span>
                  <span className="sm:hidden">Supp.</span>
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TaskCard;